// Currency formatting utilities for Algerian Dinar (DZD)

/**
 * Format amount in DZD currency
 * @param amount - The amount to format
 * @param showDecimals - Whether to show decimal places (default: false)
 * @returns Formatted currency string
 */
export const formatCurrency = (amount: number, showDecimals: boolean = false): string => {
  if (isNaN(amount)) return 'DA 0';
  
  const formatter = new Intl.NumberFormat('ar-DZ', {
    style: 'currency',
    currency: 'DZD',
    minimumFractionDigits: showDecimals ? 2 : 0,
    maximumFractionDigits: showDecimals ? 2 : 0,
  });
  
  // Format and replace the currency symbol with DA for better readability
  return formatter.format(amount).replace('DZD', 'DA').trim();
};

/**
 * Format amount as simple DZD string (e.g., "1,500 DA")
 * @param amount - The amount to format
 * @param showDecimals - Whether to show decimal places (default: false for large amounts)
 * @returns Simple formatted currency string
 */
export const formatSimpleCurrency = (amount: number, showDecimals: boolean = false): string => {
  if (isNaN(amount)) return '0 DA';
  
  const formatted = new Intl.NumberFormat('fr-DZ', {
    minimumFractionDigits: showDecimals ? 2 : 0,
    maximumFractionDigits: showDecimals ? 2 : 0,
  }).format(amount);
  
  return `${formatted} DA`;
};

/**
 * Parse currency input string to number
 * @param value - The currency string to parse
 * @returns Parsed number or 0 if invalid
 */
export const parseCurrency = (value: string): number => {
  if (!value) return 0;
  
  // Remove currency symbols and spaces, keep only numbers and decimal point
  const cleaned = value.replace(/[^\d.,]/g, '').replace(',', '.');
  const parsed = parseFloat(cleaned);
  
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Get currency symbol
 * @returns Currency symbol for DZD
 */
export const getCurrencySymbol = (): string => {
  return 'DA';
};

/**
 * Format price range for filters
 * @param min - Minimum amount
 * @param max - Maximum amount (optional)
 * @returns Formatted price range string
 */
export const formatPriceRange = (min: number, max?: number): string => {
  if (max) {
    return `${formatSimpleCurrency(min)} - ${formatSimpleCurrency(max)}`;
  }
  return `${formatSimpleCurrency(min)}+`;
};
